import {
  useEffect,
  useEffectEvent,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
  Fragment,
  type Ref,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft as ArrowLeftIcon,
  CircleCheck as CircleCheckIcon,
  Mail as MailIcon,
  MoreHorizontal as MoreHorizontalIcon,
  PanelRightOpen as PanelRightOpenIcon,
  RotateCcw as RotateCcwIcon,
  UserPlus as UserPlusIcon,
  UserRound as UserRoundIcon,
  X as CloseIcon,
  Download as DownloadIcon,
  type LucideIcon,
} from "lucide-react";
import type {
  ContactGroupSummary,
  ContactPhoneMatch,
  ConversationListItemDto,
  ConversationTimelineEventDto,
  CreateTemplateMessageParameter,
  FreeformSendBlockedReason,
  MessageDto,
  OutboundMediaCapabilitiesResponse,
} from "@multi-chat/shared";
import { listContactGroups, getProfilePhoneMatch } from "../../../api/contacts-api.js";
import { useOrganizationPlatformFlagState } from "../../organizations/use-organization-platform-flags.js";
import {
  isComposerBlockedByPlatformEntitlement,
  isPlatformNotEnabledCode,
  type PlatformEntitlementRejection,
} from "../composer-platform-entitlement.js";
import { getOutboundMediaCapabilities } from "../../../api/outbound-media-api.js";
import { channelLabelI18nKey } from "../../../components/channel/channel-presentation.js";
import { ProductState } from "../../../components/common/ProductState/index.js";
import { ErrorState } from "../../../components/common/ErrorState/index.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { useMinWidth } from "../../../components/layout/use-min-width.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogElevatedContent,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { Button } from "../../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../../components/ui/sheet/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { ContactStateMark } from "../ContactStateMark.js";
import { ConversationAvatar } from "../ConversationAvatar/ConversationAvatar.js";
import { ConversationSystemEventPill } from "../ConversationSystemEventPill.js";
import { mergeConversationTimeline } from "../merge-conversation-timeline.js";
import { AssigneePickerList, ConversationAssigneeControl } from "../ConversationAssigneeControl.js";
import { ConversationStatusControl, isWorkflowClosed } from "../ConversationStatusControl.js";
import { InboxIconButton } from "../InboxIconButton.js";
import { InboxSelectState, InboxZeroState } from "../InboxEmptyStates.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import { conversationHeaderSecondaryIdentity } from "../conversation-header-timing.js";
import { conversationComposerPresentation } from "../composer-capability.js";
import { shouldShowConversationDateSeparator } from "../conversation-date.js";
import { SendTemplateSheet } from "../SendTemplateSheet/index.js";
import type { TemplateDrawerFrame } from "../template-picker.js";
import type { ContactActionStatus, InboxMessagesView, SaveContactInput, SendMessageStatus } from "../use-inbox.js";
import { ContactGroupMultiSelect } from "../../contacts/ContactGroupMultiSelect/index.js";
import { ContactFormField, ContactFormSection } from "../../contacts/ContactFormField.js";
import { ContactsModalContent } from "../../contacts/ContactsModalContent.js";
import {
  contactFormTwoColClass,
  contactSheetBodyClass,
  contactSheetDescriptionClass,
  contactSheetFooterClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../../contacts/contact-sheet-presentation.js";
import { ContactBirthdayField } from "../../contacts/ContactBirthdayField.js";
import { ContactSearchPicker } from "../../contacts/ContactSearchPicker.js";
import { mapContactError } from "../../contacts/contact-errors.js";
import { contactLimitFeedback } from "../../contacts/contact-errors.js";
import { useOptionalNotifications } from "../../notifications/notification-context.js";
import { InternationalPhoneField } from "../../phone/InternationalPhoneField.js";
import { useDerivedDisplayName } from "../../contacts/use-derived-display-name.js";
import {
  contactGroupSelectionToRequest,
  emptyContactGroupSelection,
  type ContactGroupSelectionValue,
} from "../../contacts/contact-group-selection-value.js";
import {
  conversationTitle,
  isOutboundMessage,
  isPhoneLikeDisplay,
} from "../inbox-display.js";
import { CommentOriginBanner } from "../comments/CommentOriginBanner.js";
import { MessageBubble, continuesMessageRun } from "../MessageBubble/index.js";
import { supportsOutboundNativeReply } from "../outbound-native-reply-capability.js";
import { scrollMessageRowIntoView } from "../conversation-message-anchor.js";
import { conversationSwitchMotion } from "./conversation-switch-motion.js";
import { ConversationComposer } from "./ConversationComposer.js";
import { ConversationDateSeparator } from "./ConversationDateSeparator.js";
import { ConversationReplyWindowChip } from "./ConversationReplyWindowChip.js";
import { ConversationStartMarker } from "./ConversationStartMarker.js";
import { ConversationUnreadSeparator } from "./ConversationUnreadSeparator.js";
import { ConversationMessagesSkeleton } from "./ConversationMessagesSkeleton.js";
import { ConversationScrollPane } from "./ConversationScrollPane.js";
import { ScrollToLatestButton } from "./ScrollToLatestButton.js";
import { systemEventScrollIdentity } from "../conversation-scroll.js";
import { useConversationScrollState } from "./use-conversation-scroll-state.js";
import { useUnreadSeparator } from "./use-unread-separator.js";

/** Centered dialog from this width; full-width bottom sheet on narrower phones. */
const SAVE_CONTACT_DIALOG_MIN_WIDTH_PX = 640;

type SaveContactTab = "create" | "link";
type SaveContactSurface = "dialog" | "sheet";

/** Contact actions the Details panel triggers through the thread's existing handlers. */
export interface ConversationContactActions {
  openSave: () => void;
  openRestore: () => void;
}

const THREAD_MENU_LABEL_CLASS =
  "px-2.5 pt-2 pb-1 text-[0.6875rem] font-semibold tracking-[0.04em] text-inbox-subtle";
const THREAD_MENU_ITEM_CLASS =
  "min-h-9 gap-2.5 rounded-lg px-2.5 text-[0.8125rem] text-inbox-text data-[highlighted]:bg-inbox-hover";

/** Planned action shown in the thread menu, visibly unavailable until it ships. */
function ThreadMenuSoonItem({ icon: Icon, label }: { icon: LucideIcon; label: string }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem disabled className={cn(THREAD_MENU_ITEM_CLASS, "data-[disabled]:opacity-100")}>
      <Icon size={16} className="text-inbox-subtle" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-inbox-subtle">{label}</span>
      <span className="shrink-0 rounded-full bg-inbox-pill px-1.5 py-0.5 text-[0.65625rem] font-semibold text-inbox-muted">
        {t("inbox.comingSoon")}
      </span>
    </DropdownMenuItem>
  );
}

const fieldErrorClass = "m-0 text-[0.8rem] text-destructive";
const actionErrorClass =
  "flex flex-[1_1_100%] flex-wrap items-center gap-2 text-[0.8rem] text-destructive";

interface ConversationThreadProps {
  conversation: ConversationListItemDto | null;
  organizationId: number;
  messagesView: InboxMessagesView;
  /** Persisted ConversationEvent rows for this thread (Order 38). */
  conversationEvents?: ConversationTimelineEventDto[];
  contactActionStatus: ContactActionStatus;
  sendStatus: SendMessageStatus;
  /**
   * False while details for this conversation are not loaded, or still belong to
   * another conversation. Unknown must not be rendered as closed or unsupported.
   */
  composerCapabilityKnown: boolean;
  /** ISO timestamp or null from conversation details; drives WhatsApp composer. */
  customerServiceWindowExpiresAt: string | null;
  /** Backend-authoritative free-form eligibility (required for Facebook). */
  canSendFreeformText: boolean;
  freeformSendBlockedReason: FreeformSendBlockedReason | null;
  /** Latest real inbound customer message; header timing for non-WhatsApp. */
  lastInboundCustomerMessageAt: string | null;
  /** When false, WhatsApp is disconnected/not ready — hide template send. */
  canSendTemplate: boolean;
  /** Account behind the conversation's endpoint, named in the outbound channel disc tooltip. */
  connectedAccountName: string | null;
  hasOlderMessages: boolean;
  loadingOlderMessages: boolean;
  onRetry: () => void;
  onLoadOlderMessages: () => boolean;
  ensureQuotedMessageLoaded: (messageId: string) => Promise<boolean>;
  onSaveContact: (input: SaveContactInput) => Promise<{ ok: boolean; code?: string }>;
  onRestoreContact: () => Promise<{ ok: boolean; code?: string }>;
  onClearContactActionError: () => void;
  onClearSendError: () => void;
  onSendMessage: (
    content: string,
    options?: { replyToMessageId?: string; attachmentId?: string },
  ) => Promise<boolean>;
  onSendTemplateMessage: (input: {
    whatsappTemplateId: number;
    parameters: CreateTemplateMessageParameter[];
    clientIdempotencyKey: string;
  }) => Promise<boolean>;
  /** Header Details opener; shown only while Details is closed (always on mobile). */
  showDetailsTrigger?: boolean;
  detailsExpanded?: boolean;
  onOpenDetails?: () => void;
  detailsTriggerRef?: RefObject<HTMLButtonElement | null>;
  /** Mobile Inbox: Back to Conversation List. */
  showMobileBack?: boolean;
  onMobileBack?: () => void;
  /** zero: no conversations exist. select: conversations exist but none is open. */
  inboxPlaceholder?: "zero" | "select";
  contactActionsRef?: Ref<ConversationContactActions>;
  /** Desktop anchoring for the template picker (thread + docked Details). */
  templateDrawerFrame?: TemplateDrawerFrame | null;
  assignableMembers?: import("@multi-chat/shared").AssignableMemberDto[];
  workflowPending?: boolean;
  onAssign?: (memberId: number | null) => Promise<{ ok: boolean; message?: string }>;
  onSetStatus?: (status: "OPEN" | "CLOSED") => Promise<{ ok: boolean; message?: string }>;
  onMarkUnread?: () => Promise<{ ok: boolean; message?: string }>;
  onExport?: (format: "csv" | "xlsx") => Promise<{ ok: boolean; message?: string }>;
}

export function ConversationThread({
  conversation,
  organizationId,
  messagesView,
  conversationEvents = [],
  contactActionStatus,
  sendStatus,
  composerCapabilityKnown,
  customerServiceWindowExpiresAt,
  canSendFreeformText,
  freeformSendBlockedReason,
  lastInboundCustomerMessageAt,
  canSendTemplate,
  connectedAccountName,
  hasOlderMessages,
  loadingOlderMessages,
  onRetry,
  onLoadOlderMessages,
  ensureQuotedMessageLoaded,
  onSaveContact,
  onRestoreContact,
  onClearContactActionError,
  onClearSendError,
  onSendMessage,
  onSendTemplateMessage,
  showDetailsTrigger = false,
  detailsExpanded = false,
  onOpenDetails,
  detailsTriggerRef,
  showMobileBack = false,
  onMobileBack,
  inboxPlaceholder = "select",
  contactActionsRef,
  templateDrawerFrame = null,
  assignableMembers = [],
  workflowPending = false,
  onAssign,
  onSetStatus,
  onMarkUnread,
  onExport,
}: ConversationThreadProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const {
    flags: platformFlags,
    version: platformFlagsVersion,
    refresh: refreshPlatformFlags,
  } = useOrganizationPlatformFlagState(organizationId);
  const [entitlementRejection, setEntitlementRejection] =
    useState<PlatformEntitlementRejection | null>(null);
  const platformDisabled =
    conversation != null &&
    isComposerBlockedByPlatformEntitlement({
      flags: platformFlags,
      flagsVersion: platformFlagsVersion,
      organizationId,
      conversationId: conversation.conversationId,
      platform: conversation.platformType,
      rejection: entitlementRejection,
    });
  const notifications = useOptionalNotifications();
  const reduceMotion = useReducedMotion();
  const isRtl = i18n.dir() === "rtl";
  const saveSurface: SaveContactSurface = useMinWidth(SAVE_CONTACT_DIALOG_MIN_WIDTH_PX) ? "dialog" : "sheet";
  const { variants: switchVariants, transition: switchTransition } = conversationSwitchMotion({
    isRtl,
    reduceMotion: Boolean(reduceMotion),
  });
  const [saveFormOpen, setSaveFormOpen] = useState(false);
  const [saveTab, setSaveTab] = useState<SaveContactTab>("create");
  const {
    firstName,
    lastName,
    displayName,
    setFirstName,
    setLastName,
    setDisplayName,
    reset: resetNameFields,
  } = useDerivedDisplayName();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryIso, setPhoneCountryIso] = useState("IL");
  const [birthday, setBirthday] = useState("");
  const [selectedLinkContactId, setSelectedLinkContactId] = useState<number | null>(null);
  const [phoneMatches, setPhoneMatches] = useState<ContactPhoneMatch[]>([]);
  const [confirmPhoneReplace, setConfirmPhoneReplace] = useState(false);
  const [pendingLinkContactId, setPendingLinkContactId] = useState<number | null>(null);
  const [contactGroups, setContactGroups] = useState<ContactGroupSummary[]>([]);
  const [contactGroupsLoading, setContactGroupsLoading] = useState(false);
  const [groupSelection, setGroupSelection] = useState<ContactGroupSelectionValue>(
    emptyContactGroupSelection(),
  );
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [templateSheetOpen, setTemplateSheetOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState<MessageDto | null>(null);
  const [recordingStopped, setRecordingStopped] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const workflowClosed = conversation != null && isWorkflowClosed(conversation.status);

  useEffect(() => {
    setRecordingStopped(false);
  }, [conversation?.conversationId]);

  useEffect(() => {
    if (!workflowClosed) return;
    setTemplateSheetOpen(false);
    setReplyTarget(null);
  }, [workflowClosed, conversation?.conversationId]);
  const [mediaCapabilities, setMediaCapabilities] =
    useState<OutboundMediaCapabilitiesResponse | null>(null);
  const templateTriggerRef = useRef<HTMLButtonElement | null>(null);
  const moreTriggerRef = useRef<HTMLButtonElement | null>(null);
  /** Menu actions that open another layer run after the menu has released focus. */
  const pendingMenuActionRef = useRef<"save" | "restore" | null>(null);
  const saveOpenerRef = useRef<HTMLElement | null>(null);
  const displayNameId = useId();
  const composerId = useId();
  const timeline = useMemo(
    () =>
      messagesView.kind === "ready"
        ? mergeConversationTimeline(messagesView.items, conversationEvents)
        : [],
    [messagesView, conversationEvents],
  );
  const scrollIdentities = useMemo(
    () =>
      timeline.map((item) =>
        item.kind === "event"
          ? systemEventScrollIdentity(item.event.eventId)
          : {
              messageId: item.message.messageId,
              organizationMemberId: item.message.organizationMemberId,
              origin: item.message.origin,
              direction: item.message.direction,
            },
      ),
    [timeline],
  );
  const scroll = useConversationScrollState({
    conversationId: conversation?.conversationId ?? 0,
    messages: scrollIdentities,
    messagesReady: messagesView.kind === "ready",
    hasOlderMessages,
    loadingOlderMessages,
    onLoadOlderMessages,
  });
  const unreadSeparator = useUnreadSeparator({
    conversationId: conversation?.conversationId ?? null,
    unreadCount: conversation?.unreadCount ?? 0,
    messages:
      messagesView.kind === "ready" &&
      messagesView.items[0]?.conversationId === conversation?.conversationId
        ? messagesView.items
        : null,
  });

  const handleJumpToQuotedMessage = useCallback(
    async (messageId: string): Promise<void> => {
      const loaded = await ensureQuotedMessageLoaded(messageId);
      if (!loaded) {
        return;
      }
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
      });
      const scrollEl = scroll.getActiveScrollElement();
      if (!scrollEl) {
        return;
      }
      scrollMessageRowIntoView(scrollEl, messageId);
    },
    [ensureQuotedMessageLoaded, scroll],
  );

  useEffect(() => {
    setSaveFormOpen(false);
    setSaveTab("create");
    resetNameFields({});
    setEmail("");
    setBirthday("");
    setPhone("");
    setPhoneCountryIso("IL");
    setSelectedLinkContactId(null);
    setPhoneMatches([]);
    setConfirmPhoneReplace(false);
    setPendingLinkContactId(null);
    setGroupSelection(emptyContactGroupSelection());
    setContactGroups([]);
    setConfirmRestore(false);
    setTemplateSheetOpen(false);
    setReplyTarget(null);
    setMediaCapabilities(null);
    setEntitlementRejection(null);
    onClearContactActionError();
    onClearSendError();
  }, [conversation?.conversationId, resetNameFields, onClearContactActionError, onClearSendError]);

  const conversationId = conversation?.conversationId ?? null;
  const conversationPlatform = conversation?.platformType ?? null;
  const handlePlatformNotEnabled = useCallback(() => {
    if (conversationId == null || conversationPlatform == null) return;
    setEntitlementRejection({
      organizationId,
      conversationId,
      platform: conversationPlatform,
      flagsVersion: platformFlagsVersion,
    });
    setTemplateSheetOpen(false);
    refreshPlatformFlags();
  }, [conversationId, conversationPlatform, organizationId, platformFlagsVersion, refreshPlatformFlags]);

  const recordSendRejection = useEffectEvent((status: SendMessageStatus) => {
    if (status.kind === "error" && isPlatformNotEnabledCode(status.code)) {
      handlePlatformNotEnabled();
    }
  });
  useEffect(() => {
    recordSendRejection(sendStatus);
  }, [sendStatus]);

  useEffect(() => {
    if (conversation == null) {
      setMediaCapabilities(null);
      return;
    }
    const controller = new AbortController();
    void getOutboundMediaCapabilities(organizationId, conversation.conversationId, controller.signal)
      .then(setMediaCapabilities)
      .catch(() => {
        if (!controller.signal.aborted) {
          setMediaCapabilities(null);
        }
      });
    return () => controller.abort();
  }, [conversation?.conversationId, organizationId]);

  const openSaveForm = useCallback(
    (): void => {
      if (conversation == null) return;
      const profile = conversation.contactPlatformProfile;
      saveOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      onClearContactActionError();
      resetNameFields({ displayName: defaultSaveDisplayName(profile) });
      setEmail("");
      setBirthday("");
      setPhone(profile.normalizedPhoneNumber ?? "");
      setPhoneCountryIso("IL");
      setSaveTab("create");
      setSelectedLinkContactId(null);
      setPhoneMatches([]);
      setConfirmPhoneReplace(false);
      setPendingLinkContactId(null);
      setGroupSelection(emptyContactGroupSelection());
      setSaveFormOpen(true);
      setContactGroupsLoading(true);
      void listContactGroups(organizationId)
        .then(setContactGroups)
        .catch(() => setContactGroups([]))
        .finally(() => setContactGroupsLoading(false));
      if (conversation.platformType === "WHATSAPP") {
        void getProfilePhoneMatch(organizationId, profile.id)
          .then((response) => setPhoneMatches(response.matches))
          .catch(() => setPhoneMatches([]));
      }
    },
    [conversation, onClearContactActionError, organizationId, resetNameFields],
  );

  useImperativeHandle(
    contactActionsRef,
    () => ({
      openSave: () => {
        if (conversation?.contactPlatformProfile.contactState !== "NOT_SAVED") return;
        openSaveForm();
      },
      openRestore: () => {
        if (conversation?.contactPlatformProfile.contactState !== "DELETED") return;
        onClearContactActionError();
        setConfirmRestore(true);
      },
    }),
    [conversation, openSaveForm, onClearContactActionError],
  );

  // Instagram Login does not support outbound native Reply; drop any stale target.
  useEffect(() => {
    if (conversation != null && !supportsOutboundNativeReply(conversation.platformType)) {
      setReplyTarget(null);
    }
  }, [conversation?.conversationId, conversation?.platformType]);

  if (messagesView.kind === "idle" || conversation == null) {
    return (
      <div className="grid h-full place-items-center overflow-hidden bg-surface py-6">
        {inboxPlaceholder === "zero" ? <InboxZeroState /> : <InboxSelectState />}
      </div>
    );
  }

  const activeConversation = conversation;

  async function handleComposerSend(
    content: string,
    options?: { attachmentId?: string },
  ): Promise<boolean> {
    scroll.markLocalSend();
    const canNativeReply = supportsOutboundNativeReply(activeConversation.platformType);
    const ok = await onSendMessage(content, {
      ...(canNativeReply && replyTarget
        ? { replyToMessageId: replyTarget.messageId }
        : {}),
      ...(options?.attachmentId ? { attachmentId: options.attachmentId } : {}),
    });
    if (!ok) {
      scroll.clearLocalSend();
      return false;
    }
    setReplyTarget(null);
    return true;
  }

  async function handleComposerTemplateSend(input: {
    whatsappTemplateId: number;
    parameters: CreateTemplateMessageParameter[];
    clientIdempotencyKey: string;
  }): Promise<boolean> {
    scroll.markLocalSend();
    const ok = await onSendTemplateMessage(input);
    if (!ok) scroll.clearLocalSend();
    return ok;
  }

  const title = conversationTitle(conversation, t);
  const profile = conversation.contactPlatformProfile;
  const contactState = profile.contactState;
  const secondaryIdentity = conversationHeaderSecondaryIdentity({
    title,
    platformType: conversation.platformType,
    normalizedPhoneNumber: profile.normalizedPhoneNumber,
    platformUsername: profile.platformUsername,
  });
  const headerIdentity =
    secondaryIdentity ?? t(`channel.${channelLabelI18nKey(conversation.platformType)}`);
  const submitting = contactActionStatus.kind === "submitting";
  const actionError =
    contactActionStatus.kind === "error"
      ? mapContactError(t, contactActionStatus, t("inbox.unreachable"))
      : null;
  const hasContactIdentity =
    saveTab === "link"
      ? selectedLinkContactId != null
      : [displayName, firstName, lastName, email, phone].some((value) => value.trim().length > 0);
  const identityInvalid = saveFormOpen && !hasContactIdentity;
  const nowMs = Date.now();
  const sending = sendStatus.kind === "submitting";
  const sendError = sendStatus.kind === "error" ? sendStatus.message : null;
  const composerPresentation = composerCapabilityKnown
    ? conversationComposerPresentation({
        platformType: conversation.platformType,
        customerServiceWindowExpiresAt,
        canSendFreeformText,
        freeformSendBlockedReason,
        nowMs,
        orgCanSendTemplate: canSendTemplate,
      })
    : null;
  const composerMode = composerPresentation?.mode ?? "whatsapp_freeform";
  const showTemplateAction = composerPresentation?.showTemplateAction ?? false;

  function closeSaveForm(): void {
    setSaveFormOpen(false);
    setSaveTab("create");
    resetNameFields({});
    setEmail("");
    setBirthday("");
    setPhone("");
    setPhoneCountryIso("IL");
    setSelectedLinkContactId(null);
    setPhoneMatches([]);
    setConfirmPhoneReplace(false);
    setPendingLinkContactId(null);
    setGroupSelection(emptyContactGroupSelection());
    onClearContactActionError();
  }

  async function submitSave(options?: { confirmWhatsAppPhoneReplacement?: boolean }): Promise<void> {
    if (!hasContactIdentity || submitting) return;

    if (saveTab === "link" && selectedLinkContactId != null) {
      const result = await onSaveContact({
        mode: "LINK_EXISTING",
        contactId: selectedLinkContactId,
        confirmWhatsAppPhoneReplacement: options?.confirmWhatsAppPhoneReplacement,
      });
      if (result.ok) {
        closeSaveForm();
        return;
      }
      notifyContactLimit(result.code);
      if (result.code === "CONTACT_WHATSAPP_PHONE_CONFLICT") {
        setPendingLinkContactId(selectedLinkContactId);
        setConfirmPhoneReplace(true);
      }
      return;
    }

    const result = await onSaveContact({
      mode: "CREATE_NEW",
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      displayName: displayName || undefined,
      email: email || undefined,
      phone: phone || undefined,
      phoneCountryIso,
      birthday: birthday || undefined,
      ...contactGroupSelectionToRequest(groupSelection),
    });
    if (result.ok) {
      notifications?.pushNotification({ message: t("contacts.notices.created") });
      closeSaveForm();
      return;
    }
    notifyContactLimit(result.code);
  }

  function notifyContactLimit(code: string | undefined): void {
    if (code !== "CONTACT_LIMIT_REACHED") return;
    const copy = contactLimitFeedback(t, "create");
    notifications?.pushNotification({
      kind: "error",
      message: copy.title,
      detail: copy.message,
    });
  }

  async function handleSaveSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    await submitSave();
  }

  function handleSaveFormOpenChange(open: boolean): void {
    if (open) {
      openSaveForm();
      return;
    }
    if (submitting) return;
    closeSaveForm();
  }

  function handleSaveFormOpenAutoFocus(event: Event): void {
    event.preventDefault();
    document.getElementById(`${displayNameId}-first-name`)?.focus();
  }

  function handleSaveFormCloseAutoFocus(event: Event): void {
    event.preventDefault();
    const opener = saveOpenerRef.current;
    saveOpenerRef.current = null;
    const target = opener?.isConnected && opener !== document.body ? opener : moreTriggerRef.current;
    target?.focus();
  }

  const saveContactForm = (
    <SaveContactForm
      surface={saveSurface}
      displayNameId={displayNameId}
      saveTab={saveTab}
      onSaveTabChange={setSaveTab}
      firstName={firstName}
      lastName={lastName}
      displayName={displayName}
      email={email}
      phone={phone}
      phoneCountryIso={phoneCountryIso}
      birthday={birthday}
      phoneLocked={
        conversation.platformType === "WHATSAPP" && Boolean(profile.normalizedPhoneNumber)
      }
      identityInvalid={identityInvalid}
      hasContactIdentity={hasContactIdentity}
      submitting={submitting}
      actionError={actionError}
      contactGroups={contactGroups}
      contactGroupsLoading={contactGroupsLoading}
      groupSelection={groupSelection}
      phoneMatches={phoneMatches}
      organizationId={organizationId}
      selectedLinkContactId={selectedLinkContactId}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onDisplayNameChange={setDisplayName}
      onEmailChange={setEmail}
      onPhoneChange={setPhone}
      onPhoneCountryIsoChange={setPhoneCountryIso}
      onBirthdayChange={setBirthday}
      onGroupSelectionChange={setGroupSelection}
      onSelectLinkContact={setSelectedLinkContactId}
      onCancel={closeSaveForm}
      onSubmit={handleSaveSubmit}
      onRetry={() => void submitSave()}
    />
  );

  async function handleRestoreConfirm(): Promise<void> {
    const result = await onRestoreContact();
    if (result.ok) {
      notifications?.pushNotification({ message: t("contacts.notices.restored") });
      setConfirmRestore(false);
      return;
    }
    notifyContactLimit(result.code);
  }

  const saveFormEnabled = contactState === "NOT_SAVED";

  function runPendingMenuAction(): void {
    const action = pendingMenuActionRef.current;
    pendingMenuActionRef.current = null;
    switch (action) {
      case null:
        return;
      case "save":
        openSaveForm();
        return;
      case "restore":
        onClearContactActionError();
        setConfirmRestore(true);
        return;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  }

  const moreMenuTrigger = (
    <DropdownMenuTrigger asChild>
      <InboxIconButton ref={moreTriggerRef} aria-label={t("inbox.thread.more")}>
        <MoreHorizontalIcon size={18} aria-hidden="true" />
      </InboxIconButton>
    </DropdownMenuTrigger>
  );

  const moreMenu = (
    <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
      <SimpleTooltip content={t("inbox.thread.more")}>
        {moreMenuTrigger}
      </SimpleTooltip>
      <DropdownMenuContent
        className="w-[17.5rem] rounded-xl border-inbox-line p-1.5 shadow-inbox-pop"
        collisionPadding={12}
        onCloseAutoFocus={(event) => {
          if (pendingMenuActionRef.current == null) return;
          event.preventDefault();
          runPendingMenuAction();
        }}
      >
        <DropdownMenuLabel className={THREAD_MENU_LABEL_CLASS}>{t("inbox.thread.menu.conversation")}</DropdownMenuLabel>
        {onSetStatus && conversation ? (
          <DropdownMenuItem
            className={THREAD_MENU_ITEM_CLASS}
            disabled={workflowPending}
            onSelect={() => {
              void onSetStatus(isWorkflowClosed(conversation.status) ? "OPEN" : "CLOSED").then((result) => {
                if (!result.ok && result.message) notifications?.pushNotification({ message: result.message });
              });
            }}
          >
            {isWorkflowClosed(conversation.status) ? (
              <RotateCcwIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            ) : (
              <CircleCheckIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate">
              {isWorkflowClosed(conversation.status)
                ? t("inbox.workflow.status.reopen")
                : t("inbox.thread.closeConversation")}
            </span>
          </DropdownMenuItem>
        ) : (
          <ThreadMenuSoonItem icon={CircleCheckIcon} label={t("inbox.thread.closeConversation")} />
        )}
        {onMarkUnread ? (
          <DropdownMenuItem
            className={THREAD_MENU_ITEM_CLASS}
            disabled={workflowPending}
            data-testid="thread-menu-mark-unread"
            onSelect={() => {
              void onMarkUnread().then((result) => {
                if (!result.ok && result.message) notifications?.pushNotification({ message: result.message });
              });
            }}
          >
            <MailIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{t("inbox.thread.markUnread")}</span>
          </DropdownMenuItem>
        ) : (
          <ThreadMenuSoonItem icon={MailIcon} label={t("inbox.thread.markUnread")} />
        )}
        <DropdownMenuSeparator className="bg-inbox-line-soft" />
        <DropdownMenuLabel className={THREAD_MENU_LABEL_CLASS}>{t("inbox.thread.menu.assignment")}</DropdownMenuLabel>
        {onAssign && conversation ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn(THREAD_MENU_ITEM_CLASS, "h-auto items-start py-1.5")}
              data-testid="thread-menu-assign"
            >
              <UserRoundIcon size={16} className="mt-0.5 shrink-0 text-inbox-muted" aria-hidden="true" />
              <span className="flex min-w-0 flex-1 flex-col items-start gap-px">
                <span className="whitespace-nowrap">{t("inbox.thread.assignTo")}</span>
                {conversation.assignedMember ? (
                  <span
                    className="max-w-full truncate text-[0.75rem] font-medium text-inbox-muted"
                    title={conversation.assignedMember.displayName}
                  >
                    {conversation.assignedMember.displayName}
                  </span>
                ) : null}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              className="w-64 rounded-xl border-inbox-line p-0 shadow-inbox-pop"
              sideOffset={8}
              collisionPadding={12}
            >
              <AssigneePickerList
                assignedMember={conversation.assignedMember}
                members={assignableMembers}
                onAssign={(memberId) => {
                  setMoreMenuOpen(false);
                  return onAssign(memberId);
                }}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : (
          <ThreadMenuSoonItem icon={UserRoundIcon} label={t("inbox.thread.assignTo")} />
        )}
        <DropdownMenuSeparator className="bg-inbox-line-soft" />
        <DropdownMenuLabel className={THREAD_MENU_LABEL_CLASS}>{t("inbox.thread.menu.more")}</DropdownMenuLabel>
        {contactState === "NOT_SAVED" ? (
          <DropdownMenuItem
            className={THREAD_MENU_ITEM_CLASS}
            data-testid="thread-menu-save-contact"
            onSelect={() => {
              pendingMenuActionRef.current = "save";
            }}
          >
            <UserPlusIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{t("inbox.saveContact.action")}</span>
          </DropdownMenuItem>
        ) : null}
        {contactState === "DELETED" ? (
          <DropdownMenuItem
            className={THREAD_MENU_ITEM_CLASS}
            data-testid="thread-menu-restore-contact"
            disabled={submitting}
            onSelect={() => {
              pendingMenuActionRef.current = "restore";
            }}
          >
            <RotateCcwIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{t("inbox.restoreContact.action")}</span>
          </DropdownMenuItem>
        ) : null}
        {onExport ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={THREAD_MENU_ITEM_CLASS} data-testid="thread-menu-export">
              <DownloadIcon size={16} className="text-inbox-muted" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{t("inbox.workflow.export.label")}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              className="min-w-36 rounded-xl border-inbox-line p-1.5 shadow-inbox-pop"
              sideOffset={8}
              collisionPadding={12}
            >
              <DropdownMenuItem
                className={THREAD_MENU_ITEM_CLASS}
                onSelect={() => {
                  void onExport("csv").then((result) => {
                    if (!result.ok && result.message) notifications?.pushNotification({ message: result.message });
                  });
                }}
              >
                {t("inbox.workflow.export.csv")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={THREAD_MENU_ITEM_CLASS}
                onSelect={() => {
                  void onExport("xlsx").then((result) => {
                    if (!result.ok && result.message) notifications?.pushNotification({ message: result.message });
                  });
                }}
              >
                {t("inbox.workflow.export.excel")}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <section
      className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-inbox-thread"
      aria-label={t("inbox.threadLabel")}
    >
      <header
        className={cn(
          "relative z-[2] flex min-h-[60px] shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-inbox-line-soft bg-surface py-2 ps-4 pe-2.5",
          showMobileBack && "gap-x-2 ps-1.5 pe-1.5",
        )}
      >
        {showMobileBack && onMobileBack ? (
          <SimpleTooltip content={t("inbox.back")}>
            <InboxIconButton aria-label={t("inbox.back")} onClick={onMobileBack}>
              <ArrowLeftIcon size={19} className="rtl:rotate-180" aria-hidden="true" />
            </InboxIconButton>
          </SimpleTooltip>
        ) : null}

        <ConversationAvatar
          title={title}
          imageUrl={profile.platformProfileImageUrl}
          platformType={conversation.platformType}
          toneSeed={profile.id}
          size="sm"
        />

        <div className="@container flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <h2
              className="m-0 min-w-0 truncate text-[0.90625rem] leading-tight font-semibold tracking-[-0.012em] text-inbox-strong rtl:tracking-normal"
              dir="auto"
            >
              {isPhoneLikeDisplay(title) ? (
                <LtrValue className="truncate">{title}</LtrValue>
              ) : (
                title
              )}
            </h2>
            <ContactStateMark state={contactState} showSaved size={15} />
          </div>

          <div
            className="flex min-w-0 items-center gap-1.5 text-[0.75rem] leading-snug text-inbox-muted"
            data-testid="thread-header-identity"
          >
            <span className={cn("min-w-0 truncate", isPhoneLikeDisplay(headerIdentity) && "tabular-nums")}>
              {isPhoneLikeDisplay(headerIdentity) ? (
                <LtrValue className="truncate">{headerIdentity}</LtrValue>
              ) : (
                <bdi>{headerIdentity}</bdi>
              )}
            </span>
            {composerCapabilityKnown ? (
              <ConversationReplyWindowChip
                platformType={conversation.platformType}
                customerServiceWindowExpiresAt={customerServiceWindowExpiresAt}
                lastInboundCustomerMessageAt={lastInboundCustomerMessageAt}
                metaWindowClosed={
                  !canSendFreeformText && freeformSendBlockedReason === "WINDOW_CLOSED"
                }
                withSeparator
                className="@max-[25rem]:shrink"
              />
            ) : null}
          </div>
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-1.5">
          {conversation && onAssign ? (
            <ConversationAssigneeControl
              assignedMember={conversation.assignedMember}
              members={assignableMembers}
              pending={workflowPending}
              compact={showMobileBack}
              onAssign={onAssign}
            />
          ) : null}
          {conversation && onSetStatus ? (
            <ConversationStatusControl
              status={conversation.status}
              pending={workflowPending}
              onSetStatus={(status) => {
                void onSetStatus(status).then((result) => {
                  if (!result.ok && result.message) notifications?.pushNotification({ message: result.message });
                });
              }}
            />
          ) : null}
          {moreMenu}

          {showDetailsTrigger && onOpenDetails ? (
            <SimpleTooltip content={t("inbox.details.expand")}>
              <InboxIconButton
                ref={detailsTriggerRef}
                aria-label={t("inbox.details.expand")}
                aria-controls="inbox-details"
                aria-expanded={detailsExpanded}
                onClick={onOpenDetails}
              >
                <PanelRightOpenIcon size={18} className="rtl:-scale-x-100" aria-hidden="true" />
              </InboxIconButton>
            </SimpleTooltip>
          ) : null}
        </div>

        {!saveFormOpen && actionError && contactState === "DELETED" ? (
          <div className={cn(actionErrorClass, "basis-full")} role="alert">
            <p className="m-0">{actionError}</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => void handleRestoreConfirm()}
              disabled={submitting}
            >
              {t("inbox.retry")}
            </Button>
          </div>
        ) : null}
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-hidden bg-inbox-thread">
          <AnimatePresence initial={false}>
            <motion.div
              key={conversation.conversationId}
              className="absolute inset-0 overflow-hidden"
              variants={switchVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={switchTransition}
            >
              <ConversationScrollPane
                onActiveScrollElementChange={scroll.setActiveScrollElement}
                onScroll={scroll.handleMessagesScroll}
                className={cn(
                  "h-full min-h-0 overflow-y-auto px-5 pt-4 pb-3 max-md:px-3",
                  INBOX_SCROLLER_CLASS,
                )}
                aria-busy={messagesView.kind === "loading" ? true : undefined}
              >
              {messagesView.kind === "loading" ? <ConversationMessagesSkeleton /> : null}

              {messagesView.kind === "error" ? (
                <ErrorState
                  layout="panel"
                  message={messagesView.message}
                  onRetry={onRetry}
                  retryLabel={t("inbox.retry")}
                />
              ) : null}

              {messagesView.kind === "empty" ? (
                <ProductState
                  tone="readonly"
                  title={t("inbox.emptyThreadTitle")}
                  description={t("inbox.emptyThreadDescription")}
                />
              ) : null}

              {messagesView.kind === "ready" ? (
                <ol className="m-0 flex w-full list-none flex-col gap-0.5 p-0">
                  {!hasOlderMessages && !loadingOlderMessages ? (
                    <ConversationStartMarker
                      title={title}
                      imageUrl={profile.platformProfileImageUrl}
                      platformType={conversation.platformType}
                      toneSeed={profile.id}
                    />
                  ) : null}
                  {loadingOlderMessages ? (
                    <li className="py-2 text-center text-[0.8rem] text-subtle" aria-live="polite">
                      {t("inbox.loadingOlderMessages")}
                    </li>
                  ) : null}
                  {unreadSeparator.kind === "pinned" ? (
                    <ConversationUnreadSeparator count={unreadSeparator.count} />
                  ) : null}
                  {timeline.map((item, index) => {
                    if (item.kind === "event") {
                      return (
                        <li key={`event-${item.event.eventId}`} className="list-none">
                          <ConversationSystemEventPill
                            event={item.event}
                            locale={i18n.language}
                          />
                        </li>
                      );
                    }
                    const message = item.message;
                    const previousMessage = [...timeline.slice(0, index)]
                      .toReversed()
                      .find((entry) => entry.kind === "message");
                    const previous =
                      previousMessage?.kind === "message" ? previousMessage.message : undefined;
                    const nextMessage = timeline
                      .slice(index + 1)
                      .find((entry) => entry.kind === "message");
                    const next = nextMessage?.kind === "message" ? nextMessage.message : undefined;
                    const showDateSeparator = shouldShowConversationDateSeparator(
                      previous,
                      message,
                    );
                    const unreadStartsHere =
                      unreadSeparator.kind === "before" &&
                      unreadSeparator.messageId === message.messageId;
                    const groupStart = unreadStartsHere || !continuesMessageRun(previous, message);
                    return (
                      <Fragment key={message.messageId}>
                        {showDateSeparator ? (
                          <ConversationDateSeparator
                            message={message}
                            locale={i18n.language}
                            nowMs={nowMs}
                          />
                        ) : null}
                        {unreadStartsHere ? <ConversationUnreadSeparator /> : null}
                        {message.originatingSocialComment ? (
                          <div
                            className={cn(
                              "flex",
                              isOutboundMessage(message) ? "justify-end" : "justify-start",
                            )}
                          >
                            <CommentOriginBanner origin={message.originatingSocialComment} />
                          </div>
                        ) : null}
                        <MessageBubble
                          message={message}
                          locale={i18n.language}
                          platformType={conversation.platformType}
                          organizationId={organizationId}
                          accountName={connectedAccountName}
                          turnGap={
                            previous != null && !showDateSeparator && !unreadStartsHere && groupStart
                          }
                          groupStart={groupStart}
                          groupEnd={
                            !continuesMessageRun(message, next) ||
                            (unreadSeparator.kind === "before" &&
                              unreadSeparator.messageId === next?.messageId)
                          }
                          onJumpToQuotedMessage={handleJumpToQuotedMessage}
                          onReply={
                            !workflowClosed && supportsOutboundNativeReply(conversation.platformType)
                              ? setReplyTarget
                              : undefined
                          }
                          closed={workflowClosed}
                        />
                      </Fragment>
                    );
                  })}
                </ol>
              ) : null}
              </ConversationScrollPane>
            </motion.div>
          </AnimatePresence>
        </div>
        {scroll.isAwayFromBottom ? (
          <ScrollToLatestButton
            formattedCount={scroll.formattedInboundCount}
            onClick={scroll.scrollToLatest}
          />
        ) : null}
      </div>

      <ConversationComposer
        key={conversation.conversationId}
        conversationId={conversation.conversationId}
        composerId={composerId}
        mode={composerMode}
        capabilityPending={!composerCapabilityKnown}
        platformDisabled={platformDisabled}
        showTemplate={showTemplateAction && !platformDisabled && !workflowClosed}
        sending={sending}
        sendError={!templateSheetOpen && !platformDisabled ? sendError : null}
        replyTarget={
          !workflowClosed && supportsOutboundNativeReply(conversation.platformType) ? replyTarget : null
        }
        contactLabel={title}
        organizationId={organizationId}
        mediaCapabilities={mediaCapabilities}
        onClearReply={() => setReplyTarget(null)}
        onSend={handleComposerSend}
        onOpenTemplate={() => {
          if (workflowClosed) return;
          onClearSendError();
          setTemplateSheetOpen(true);
        }}
        onClearSendError={onClearSendError}
        onPlatformNotEnabled={handlePlatformNotEnabled}
        templateTriggerRef={templateTriggerRef}
        workflowClosed={workflowClosed}
        recordingStopped={recordingStopped}
        reopenPending={workflowPending}
        onReopen={() => {
          void onSetStatus?.("OPEN").then((result) => {
            if (result && !result.ok && result.message) {
              notifications?.pushNotification({ message: result.message });
            }
          });
        }}
        onRecordingDiscarded={() => setRecordingStopped(true)}
      />

      {showTemplateAction ? (
        <SendTemplateSheet
          open={templateSheetOpen}
          organizationId={organizationId}
          conversationId={conversation.conversationId}
          sendStatus={sendStatus}
          unreachableMessage={t("inbox.unreachable")}
          onOpenChange={setTemplateSheetOpen}
          onClearSendError={onClearSendError}
          onSendTemplate={handleComposerTemplateSend}
          contactLabel={title}
          platformEndpointId={conversation.platformEndpointId}
          drawerFrame={templateDrawerFrame}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            templateTriggerRef.current?.focus();
          }}
        />
      ) : null}

      {saveFormEnabled && saveSurface === "dialog" ? (
        <AlertDialog open={saveFormOpen} onOpenChange={handleSaveFormOpenChange}>
          <ContactsModalContent
            className="w-[min(92vw,34rem)] bg-surface"
            data-testid="save-contact-dialog"
            onOpenAutoFocus={handleSaveFormOpenAutoFocus}
            onCloseAutoFocus={handleSaveFormCloseAutoFocus}
          >
            {saveContactForm}
          </ContactsModalContent>
        </AlertDialog>
      ) : null}
      {saveFormEnabled && saveSurface === "sheet" ? (
        <Sheet open={saveFormOpen} onOpenChange={handleSaveFormOpenChange}>
          <SheetContent
            side="bottom"
            className="flex max-h-[92dvh] flex-col gap-0 overflow-hidden bg-surface p-0"
            data-testid="save-contact-sheet"
            onCloseAutoFocus={handleSaveFormCloseAutoFocus}
          >
            {saveContactForm}
          </SheetContent>
        </Sheet>
      ) : null}

      <AlertDialog
        open={confirmPhoneReplace}
        onOpenChange={(open) => {
          setConfirmPhoneReplace(open);
          if (!open) {
            setPendingLinkContactId(null);
            onClearContactActionError();
          }
        }}
      >
        <AlertDialogElevatedContent>
          <AlertDialogTitle>{t("inbox.saveContact.phoneConflictTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("inbox.saveContact.phoneConflictDescription")}
          </AlertDialogDescription>
          {actionError ? (
            <p className={fieldErrorClass} role="alert">
              {actionError}
            </p>
          ) : null}
          <AlertDialogActions>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmPhoneReplace(false);
                setPendingLinkContactId(null);
              }}
              disabled={submitting}
            >
              {t("inbox.saveContact.cancel")}
            </Button>
            <Button
              onClick={() => {
                const contactId = pendingLinkContactId ?? selectedLinkContactId;
                if (contactId == null) return;
                setSelectedLinkContactId(contactId);
                void submitSave({ confirmWhatsAppPhoneReplacement: true }).then(() => {
                  setConfirmPhoneReplace(false);
                  setPendingLinkContactId(null);
                });
              }}
              disabled={submitting}
            >
              {submitting
                ? t("inbox.saveContact.saving")
                : t("inbox.saveContact.replacePhoneConfirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogElevatedContent>
      </AlertDialog>

      <AlertDialog
        open={confirmRestore}
        onOpenChange={(open) => {
          setConfirmRestore(open);
          if (!open) onClearContactActionError();
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t("inbox.restoreContact.confirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("inbox.restoreContact.confirmDescription")}
          </AlertDialogDescription>
          {actionError ? (
            <p className={fieldErrorClass} role="alert">
              {actionError}
            </p>
          ) : null}
          <AlertDialogActions>
            <Button
              variant="outline"
              onClick={() => setConfirmRestore(false)}
              disabled={submitting}
            >
              {t("inbox.restoreContact.cancel")}
            </Button>
            <Button onClick={() => void handleRestoreConfirm()} disabled={submitting}>
              {submitting
                ? t("inbox.restoreContact.restoring")
                : t("inbox.restoreContact.confirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function SaveContactHeading({ surface }: { surface: SaveContactSurface }): React.JSX.Element {
  const { t } = useTranslation();
  const title = t("inbox.saveContact.dialogTitle");
  const description = t("inbox.saveContact.dialogDescription");
  switch (surface) {
    case "dialog":
      return (
        <div className="grid min-w-0 gap-1">
          <AlertDialogTitle className={contactSheetTitleClass}>{title}</AlertDialogTitle>
          <AlertDialogDescription className={contactSheetDescriptionClass}>{description}</AlertDialogDescription>
        </div>
      );
    case "sheet":
      return (
        <div className="grid min-w-0 gap-1">
          <SheetTitle className={contactSheetTitleClass}>{title}</SheetTitle>
          <SheetDescription className={contactSheetDescriptionClass}>{description}</SheetDescription>
        </div>
      );
    default: {
      const _exhaustive: never = surface;
      return _exhaustive;
    }
  }
}

function SaveContactForm({
  surface,
  displayNameId,
  saveTab,
  onSaveTabChange,
  firstName,
  lastName,
  displayName,
  email,
  phone,
  phoneCountryIso,
  birthday,
  phoneLocked,
  identityInvalid,
  hasContactIdentity,
  submitting,
  actionError,
  contactGroups,
  contactGroupsLoading,
  groupSelection,
  phoneMatches,
  organizationId,
  selectedLinkContactId,
  onFirstNameChange,
  onLastNameChange,
  onDisplayNameChange,
  onEmailChange,
  onPhoneChange,
  onPhoneCountryIsoChange,
  onBirthdayChange,
  onGroupSelectionChange,
  onSelectLinkContact,
  onCancel,
  onSubmit,
  onRetry,
}: {
  surface: SaveContactSurface;
  displayNameId: string;
  saveTab: SaveContactTab;
  onSaveTabChange: (tab: SaveContactTab) => void;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone: string;
  phoneCountryIso: string;
  birthday: string;
  phoneLocked: boolean;
  identityInvalid: boolean;
  hasContactIdentity: boolean;
  submitting: boolean;
  actionError: string | null;
  contactGroups: ContactGroupSummary[];
  contactGroupsLoading: boolean;
  groupSelection: ContactGroupSelectionValue;
  phoneMatches: ContactPhoneMatch[];
  organizationId: number;
  selectedLinkContactId: number | null;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onPhoneCountryIsoChange: (iso: string) => void;
  onBirthdayChange: (value: string) => void;
  onGroupSelectionChange: (value: ContactGroupSelectionValue) => void;
  onSelectLinkContact: (contactId: number | null) => void;
  onCancel: () => void;
  onSubmit: (event: React.FormEvent) => void | Promise<void>;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const firstNameId = `${displayNameId}-first-name`;
  const lastNameId = `${displayNameId}-last-name`;
  const emailId = `${displayNameId}-email`;
  const errorId = `${displayNameId}-error`;
  const exactPhoneMatch = phoneMatches[0] ?? null;

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => void onSubmit(event)} noValidate>
      <header className={contactSheetHeaderClass}>
        <div className="flex min-w-0 items-start gap-3">
          <span className={contactSheetHeaderIconClass} aria-hidden="true">
            <UserPlusIcon className="size-4" />
          </span>
          <SaveContactHeading surface={surface} />
        </div>
        <Button
          type="button"
          variant="ghost"
          className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
          onClick={onCancel}
          disabled={submitting}
          aria-label={t("common.close")}
        >
          <CloseIcon className="size-4" aria-hidden="true" />
        </Button>
      </header>

      <div className={cn(contactSheetBodyClass, "grid content-start gap-4")}>
        <div className="flex gap-1 rounded-lg border border-line/70 bg-surface-muted/50 p-1" role="tablist">
          {(["create", "link"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={saveTab === tab}
              className={cn(
                "flex-1 rounded-md px-3 py-1.5 text-[0.8125rem] font-medium transition-colors",
                saveTab === tab
                  ? "bg-surface text-foreground shadow-sm"
                  : "text-muted hover:text-foreground",
              )}
              onClick={() => onSaveTabChange(tab)}
            >
              {t(`inbox.saveContact.tabs.${tab}`)}
            </button>
          ))}
        </div>

        {exactPhoneMatch ? (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[0.8125rem] text-sky-900">
            <p className="m-0">{t("inbox.saveContact.phoneMatchBanner")}</p>
            <button
              type="button"
              className="mt-1 font-medium text-sky-800 underline"
              onClick={() => {
                onSaveTabChange("link");
                onSelectLinkContact(Number(exactPhoneMatch.contactId));
              }}
            >
              {exactPhoneMatch.displayName}
            </button>
          </div>
        ) : null}

        {saveTab === "create" ? (
          <>
            <ContactFormSection legend={t("contacts.create.sections.basicDetails")}>
              <div className={contactFormTwoColClass}>
                <ContactFormField
                  id={firstNameId}
                  label={t("contacts.columns.firstName")}
                  placeholder={t("contacts.columns.firstName")}
                  value={firstName}
                  disabled={submitting}
                  onChange={onFirstNameChange}
                />
                <ContactFormField
                  id={lastNameId}
                  label={t("contacts.columns.lastName")}
                  placeholder={t("contacts.columns.lastName")}
                  value={lastName}
                  disabled={submitting}
                  onChange={onLastNameChange}
                />
                <div className="sm:col-span-2">
                  <ContactFormField
                    id={displayNameId}
                    label={t("contacts.columns.displayName")}
                    placeholder={t("contacts.columns.displayName")}
                    value={displayName}
                    disabled={submitting}
                    invalid={identityInvalid}
                    describedBy={identityInvalid || actionError ? errorId : undefined}
                    onChange={onDisplayNameChange}
                  />
                </div>
                <div className="sm:col-span-2">
                  <ContactBirthdayField
                    id={`${displayNameId}-birthday`}
                    value={birthday}
                    disabled={submitting}
                    onChange={onBirthdayChange}
                  />
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.create.sections.communication")}>
              <div className="grid gap-2.5">
                <ContactFormField
                  id={emailId}
                  label={t("contacts.columns.email")}
                  type="email"
                  placeholder={t("contacts.columns.email")}
                  value={email}
                  disabled={submitting}
                  onChange={onEmailChange}
                />
                <div className="min-w-0">
                  <InternationalPhoneField
                    value={phone}
                    defaultCountryIso={phoneCountryIso}
                    onChange={onPhoneChange}
                    onCountryIsoChange={onPhoneCountryIsoChange}
                    disabled={submitting}
                    readOnly={phoneLocked}
                    showLockIndicator={phoneLocked}
                  />
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.groups.multiSelect.label")}>
              <ContactGroupMultiSelect
                groups={contactGroups}
                value={groupSelection}
                onChange={onGroupSelectionChange}
                disabled={submitting}
                loading={contactGroupsLoading}
                presentation="section"
              />
            </ContactFormSection>
          </>
        ) : (
          <ContactFormSection legend={t("inbox.saveContact.linkExistingLabel")}>
            <ContactSearchPicker
              organizationId={organizationId}
              selectedContactId={selectedLinkContactId}
              onSelect={(contact) => onSelectLinkContact(Number(contact.id))}
              disabled={submitting}
              searchLabel={t("inbox.saveContact.linkSearchLabel")}
              searchPlaceholder={t("inbox.saveContact.linkSearchPlaceholder")}
            />
          </ContactFormSection>
        )}

        {identityInvalid ? (
          <p className={fieldErrorClass} id={errorId}>
            {saveTab === "link"
              ? t("inbox.saveContact.linkRequired")
              : t("inbox.saveContact.identityRequired")}
          </p>
        ) : null}
        {actionError ? (
          <div className={cn(actionErrorClass, "flex-[0_0_auto]")} role="alert" id={errorId}>
            <p className="m-0">{actionError}</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onRetry}
              disabled={submitting || !hasContactIdentity}
            >
              {t("inbox.retry")}
            </Button>
          </div>
        ) : null}
      </div>

      <footer
        className={cn(
          contactSheetFooterClass,
          "flex flex-wrap justify-end gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
        )}
      >
        <Button type="button" variant="outline" className="h-9" onClick={onCancel} disabled={submitting}>
          {t("inbox.saveContact.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={submitting || !hasContactIdentity}
          className="h-9 gap-1.5 text-[0.8125rem] font-semibold"
        >
          {!submitting ? <UserPlusIcon size={14} aria-hidden="true" /> : null}
          {submitting
            ? t("inbox.saveContact.saving")
            : saveTab === "link"
              ? t("inbox.saveContact.linkAction")
              : t("inbox.saveContact.save")}
        </Button>
      </footer>
    </form>
  );
}

function defaultSaveDisplayName(profile: ConversationListItemDto["contactPlatformProfile"]): string {
  return profile.platformDisplayName?.trim() || profile.normalizedPhoneNumber || "";
}
